import { useTranslation } from 'react-i18next'
import { isFromEnvironment, isWaste } from '../../../calculators/EnergyFlow'
import { BatteryProvider } from '../../../calculators/providers/BatteryProvider'
import { reduceToSumOfItems } from '../../../util/array-utility'

export type BatteryFlow = {
  name: string
  hourSeries: number[]
}

type BatteryChartUtil = {
  batteryFlows: BatteryFlow[]
  defaultFlows: BatteryFlow[]
  options: string[]
}

export const useBatteryChartUtil = (
  providers: BatteryProvider[],
): BatteryChartUtil => {
  const { t } = useTranslation()

  const batteryFlows: BatteryFlow[] = []
  const defaultFlows: BatteryFlow[] = []

  const getBatteryFlows = (provider: BatteryProvider) => {
    const remainingCapacities = {
      name: `${provider.name} (${t('charts.battery.remainingCapacity')})`,
      hourSeries: [...provider.remainingCapacityHourSeries],
    }
    batteryFlows.push(remainingCapacities)
    defaultFlows.push(remainingCapacities)

    const chargeFlows = provider.flows?.filter(isWaste)
    if (chargeFlows) {
      const combinedChargeHourSeries = chargeFlows
        .map((flow) => flow.hourSeries)
        .reduce(reduceToSumOfItems, [])

      const charging = {
        name: `${provider.name} (${t('charts.battery.charging')})`,
        hourSeries: combinedChargeHourSeries,
      }
      batteryFlows.push(charging)
    }

    const dischargeFlows = provider.flows?.filter(isFromEnvironment)
    if (dischargeFlows) {
      const combinedDishargeHourSeries = dischargeFlows
        .map((flow) => flow.hourSeries)
        .reduce(reduceToSumOfItems, [])

      const discharging = {
        name: `${provider.name} (${t('charts.battery.discharging')})`,
        hourSeries: combinedDishargeHourSeries,
      }
      batteryFlows.push(discharging)
    }
  }

  providers.forEach(getBatteryFlows)

  const overproductionHourSeries = providers
    .map((provider) => provider.overproductionHourSeries)
    .reduce(reduceToSumOfItems, [])

  const overproduction = {
    name: t('charts.battery.overproduction'),
    hourSeries: overproductionHourSeries,
  }

  batteryFlows.push(overproduction)

  const options = batteryFlows.map((flow) => flow.name)
  return { batteryFlows, defaultFlows, options }
}
